import { shortCoins } from "odyssey-shared";
import { say } from "../../words.ts";
import type { TravelOffer, TravelPlace } from "./wire.ts";

/**
 * The words travel shows (ADR-137 decisions 22 to 24): the world map's title in travel mode, each offer line's name,
 * price and reason, the offer grouped by region, and the message line for a refusal or a break. Pure: wire records in,
 * strings and plain records out; every sentence is the language files' (`travel.*`).
 */

/**
 * The refusal codes the language files word (`travel.refused.<code>`); any other reads `travel.refused.unknown`.
 */
const REFUSED = ["combat", "duel", "frozen", "transfer", "combatant", "outlaw", "dead", "busy", "short", "focus", "weary", "stale", "moved"] as const;

/**
 * The break codes with words of their own (`travel.broken.<code>`); any other break is worded as its refusal is.
 */
const BROKEN = ["damaged", "moved", "cancelled"] as const;

/**
 * The reasons an offer line is dimmed with words of their own (`travel.dimmed.<code>`).
 */
const DIMMED = ["short", "focus", "outlaw"] as const;

/**
 * One line of travel mode's list: the place's index in the offer, its words, its price, and why it is dimmed (empty
 * when it can be paid now).
 */
export interface TravelLine {
  readonly index: number;
  readonly name: string;
  readonly price: string;
  readonly focus: string;
  readonly dimmed: string;
  readonly place: TravelPlace;
}

/**
 * A region's lines under its heading, in the offer's order.
 */
export interface TravelGroup {
  readonly region: string;
  readonly lines: readonly TravelLine[];
}

/**
 * The message line for a refused travel request.
 *
 * @param reason The code the server sent.
 * @returns The sentence.
 * @example
 * refusedLine("combat");
 * // "You can't travel while in combat."
 */
export function refusedLine(reason: string): string {
  return (REFUSED as readonly string[]).includes(reason) ? say(`travel.refused.${reason as (typeof REFUSED)[number]}`) : say("travel.refused.unknown");
}

/**
 * The message line for a broken channel: its own words for a hit, a step or a cancel, else the refusal's words for the
 * rule that broke it.
 *
 * @param reason The code the server sent.
 * @returns The sentence.
 */
export function brokenLine(reason: string): string {
  if ((BROKEN as readonly string[]).includes(reason)) {
    return say(`travel.broken.${reason as (typeof BROKEN)[number]}`);
  }
  return (REFUSED as readonly string[]).includes(reason) ? refusedLine(reason) : say("travel.broken.unknown");
}

/**
 * The world map's title in travel mode: "Travel from <source>", or "Travel from here" from anywhere or from a place
 * without a name.
 *
 * @param offer The offer.
 * @returns The title.
 */
export function travelTitle(offer: TravelOffer): string {
  return offer.source === "shrine" && offer.sourceName !== "" ? say("travel.fromPlace", { name: offer.sourceName }) : say("travel.fromHere");
}

/**
 * A place's name as a line reads it: its own, or "A shrine" or "A waypoint" by its kind when it has none, followed by
 * its map's name when it is on another map.
 *
 * @param place The place.
 * @returns The name.
 */
export function placeName(place: TravelPlace): string {
  const own = place.name !== "" ? place.name : place.kind === "waypoint" ? say("travel.waypoint") : say("travel.shrine");
  return place.map === "" ? own : say("travel.otherMap", { name: own, map: place.map });
}

/**
 * The words that say why a place cannot be paid now, or an empty string when it can.
 *
 * @param place The offered place.
 * @returns The reason in words.
 */
export function dimmedWords(place: TravelPlace): string {
  if (place.affordable) {
    return "";
  }
  return (DIMMED as readonly string[]).includes(place.reason) ? say(`travel.dimmed.${place.reason as (typeof DIMMED)[number]}`) : say("travel.dimmed.unknown");
}

/**
 * The offer's lines grouped by region, regions in the order their nearest place comes, lines in the offer's order
 * (nearest first). A place with no region is grouped under the map's name.
 *
 * @param offer The offer.
 * @param mapName The name of the map the character stands on.
 * @returns The groups.
 */
export function travelGroups(offer: TravelOffer, mapName: string): readonly TravelGroup[] {
  const groups = new Map<string, TravelLine[]>();
  offer.places.forEach((place, index) => {
    const region = place.region !== "" ? place.region : place.map !== "" ? place.map : mapName;
    const line: TravelLine = {
      index,
      name: placeName(place),
      price: place.coin === 0 ? say("travel.free") : shortCoins(place.coin),
      focus: place.focus === 0 ? "" : say("travel.focus", { count: place.focus }),
      dimmed: dimmedWords(place),
      place,
    };
    const lines = groups.get(region);
    if (lines === undefined) {
      groups.set(region, [line]);
    } else {
      lines.push(line);
    }
  });
  return [...groups].map(([region, lines]) => ({ region, lines }));
}
